import React from 'react';
import { useNavigate } from 'react-router-dom';
import { parlorScreen } from '../../designs';
import OnboardingLayout from './OnboardingLayout';
import { useOnboardingData } from './useOnboardingData';
import { getInitials } from './onboardingState';

const d = parlorScreen.tokens;
const fs = parlorScreen.typography.fontSize;

function InitialsBadge({ initials }: { initials: string }) {
  return (
    <div style={{
      width: '36px', height: '36px', borderRadius: '50%',
      border: `1px solid rgba(200,146,58,0.30)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 600,
      letterSpacing: '1px', color: d.textSecondary, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

export default function OnboardingStep2Crew() {
  const navigate = useNavigate();
  const { data, setData } = useOnboardingData();

  const updateCrewMember = (index: number, name: string) => {
    const updated = [...data.crew];
    updated[index] = { ...updated[index], name, initials: getInitials(name) };
    setData({ crew: updated });
  };

  const addCrewMember = () => {
    setData({ crew: [...data.crew, { name: '', initials: '' }] });
  };

  const removeCrewMember = (index: number) => {
    setData({ crew: data.crew.filter((_, i) => i !== index) });
  };

  const toggleCoPlanner = (index: number) => {
    const updated = data.crew.map((m, i) => ({
      ...m,
      isCoPlanner: i === index ? !m.isCoPlanner : false, // only one co-planner
    }));
    setData({ crew: updated });
  };

  const coPlannerIndex = data.crew.findIndex((m) => m.isCoPlanner);

  return (
    <OnboardingLayout
      step={2}
      headline="Who's coming?"
      buttonLabel="Next"
      onSubmit={() => navigate('/new-trip/lodging')}
      onSkip={() => navigate('/new-trip/lodging')}
      onBack={() => navigate('/new-trip')}
    >
      {/* Crew rows */}
      {data.crew.map((member, index) => {
        const isThisCoPlanner = !!member.isCoPlanner;
        const anotherCoPlannerSet = coPlannerIndex !== -1 && coPlannerIndex !== index;
        return (
          <div key={index} style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="text" value={member.name}
                onChange={(e) => updateCrewMember(index, e.target.value)}
                onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
                style={{
                  flex: 1, padding: '10px 12px',
                  fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 400,
                  color: d.textPrimary, backgroundColor: d.bgSurface,
                  border: '1px solid rgba(200,146,58,0.10)', borderRadius: '2px',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
              {member.initials && <InitialsBadge initials={member.initials} />}
              <button onClick={() => removeCrewMember(index)} type="button" style={{
                width: '32px', height: '32px', borderRadius: '50%',
                border: `1px solid rgba(200,146,58,0.10)`,
                backgroundColor: 'transparent', color: d.textSecondary,
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: fs.bodyInput, flexShrink: 0,
              }}>
                ×
              </button>
            </div>
            {/* Co-planner toggle — only shown when name is entered */}
            {member.name.trim() && (
              <button
                onClick={() => toggleCoPlanner(index)}
                disabled={anotherCoPlannerSet}
                type="button"
                style={{
                  marginTop: '4px', marginLeft: '2px', padding: '2px 0',
                  background: 'none', border: 'none',
                  cursor: anotherCoPlannerSet ? 'not-allowed' : 'pointer',
                  fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 400,
                  color: isThisCoPlanner ? d.accent : d.textTertiary,
                  opacity: anotherCoPlannerSet ? 0.4 : 1,
                  textAlign: 'left',
                }}
              >
                {isThisCoPlanner ? '◆ Co-planner' : 'Make co-planner'}
              </button>
            )}
          </div>
        );
      })}

      {/* Add person button */}
      <button onClick={addCrewMember} type="button" style={{
        width: '100%', padding: '10px 16px',
        fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
        letterSpacing: '2px', textTransform: 'uppercase',
        color: d.textSecondary, backgroundColor: 'transparent',
        border: `1px dashed rgba(200,146,58,0.15)`, borderRadius: '2px',
        cursor: 'pointer', marginBottom: '24px',
      }}>
        + Add Person
      </button>

    </OnboardingLayout>
  );
}
